import React, { useState, useEffect } from 'react';
import { Modal, Button, FormInput, DataTable } from '@/components/ui';
import { Clock, PackageCheck, Truck, X } from 'lucide-react';
import toast from 'react-hot-toast';

export default function DispatchManagementModal({ isOpen, onClose, request, onReceive, onDispatch }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    driver_name: '',
    truck_number: '',
    billed_to: 'Store / Warehouse address',
    shipped_to: 'Customer / Delivery address',
    dispatch_no: '',
    invoice_no: '',
    notes: ''
  });
  const [sameAsBilledTo, setSameAsBilledTo] = useState(false);

  if (!isOpen || !request) return null;



  const handleDispatch = async () => {
    if (!formData.driver_name || !formData.truck_number) {
      toast.error('Driver name and truck number are required');
      return;
    }
    setLoading(true);
    try {
      await onDispatch(request._id, formData);
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'billed_to' && sameAsBilledTo) {
        next.shipped_to = value;
      }
      return next;
    });
  };

  const handleSameAsBilledTo = (e) => {
    const checked = e.target.checked;
    setSameAsBilledTo(checked);
    if (checked) {
      setFormData(prev => ({ ...prev, shipped_to: prev.billed_to }));
    }
  };

  const items = request.estimation_id?.items || [];
  
  const columns = [
    { header: '#', key: 'index', render: (_, i) => <span className="text-slate-500 font-medium">{i + 1}</span>, minWidth: '40px' },
    { header: 'Item Name', key: 'name', render: (l) => {
        const name = l.name || l.item_details?.product || l.item_details?.name || 'Unnamed Item';
        const w = l.width || l.item_details?.width;
        const h = l.height || l.item_details?.height;
        const dim = l.dimensions || l.item_details?.dimensions || (w && h ? `${w} x ${h}` : '');
        return <span className="text-sm font-semibold text-slate-800">{dim ? `${name} (${dim})` : name}</span>;
      }
    },
    { header: 'Thickness', key: 'thickness', render: (l) => <span className="text-sm text-slate-600">{l.thickness || l.item_details?.thickness || '-'}</span> },
    // { header: 'Quality', key: 'quality', render: (l) => <span className="text-sm text-slate-600">{l.quality || l.item_details?.quality || '-'}</span> },
    { header: 'Qty', key: 'qty', render: (l) => <span className="text-sm font-bold text-slate-700 bg-slate-100 px-2 py-0.5 rounded">{l.qty || 1}</span>, minWidth: '80px' }
  ];

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      size="lg"
      title="Dispatch Management"
      footer={
        <>
          <Button
            variant="outline"
            className="px-6 py-2.5 border-slate-200 text-slate-600 hover:bg-slate-50 rounded-xl font-semibold transition-colors"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="px-8 py-2.5 bg-slate-800 hover:bg-slate-900 text-white rounded-xl flex items-center justify-center gap-2 font-semibold shadow-md transition-all"
            onClick={handleDispatch}
            isLoading={loading}
          >
            <Truck size={18} /> Start Dispatch
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="p-5 bg-white border border-slate-200 rounded-xl flex items-center justify-between shadow-sm">
           <div className="flex items-center gap-4">
             <div className="w-12 h-12 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center">
               <Truck size={24} />
             </div>
             <div>
               <div className="flex items-center gap-2 mb-1">
                 <span className="text-slate-800 font-bold text-lg">
                   {request.customer_id?.sdId} {request.customer_id?.name ? `- ${request.customer_id.name}` : ''}
                 </span>
                 <span className="bg-blue-100 text-blue-700 text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full">Ready</span>
               </div>
               <p className="text-slate-500 text-sm">Fill in delivery details below</p>
             </div>
           </div>
        </div>

        {/* Form Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <FormInput
            label="Driver Name *"
            name="driver_name"
            value={formData.driver_name}
            onChange={handleChange}
            placeholder="Enter driver full name"
            className="bg-white shadow-sm"
            required
          />
          <FormInput
            label="Truck Number *"
            name="truck_number"
            value={formData.truck_number}
            onChange={handleChange}
            placeholder="# AB-12-CD-3456"
            className="bg-white shadow-sm"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
           <div>
             <label className="block text-xs font-bold text-slate-700 mb-1.5 flex items-center gap-1.5">
                <span className="text-brand-blue">📍</span> BILLED TO
             </label>
             <textarea 
               name="billed_to"
               value={formData.billed_to}
               onChange={handleChange}
               className="w-full text-sm p-3.5 border border-slate-200 rounded-xl outline-none focus:border-brand-blue focus:ring-2 focus:ring-blue-50 resize-none h-24 bg-white shadow-sm text-slate-700 transition-all"
             />
           </div>
           <div>
             <label className="block text-xs font-bold text-slate-700 mb-1.5 flex justify-between items-center">
               <span className="flex items-center gap-1.5">
                  <span className="text-brand-blue">📍</span> SHIPPED TO
               </span>
               <span className="flex items-center gap-1.5 text-slate-500 font-medium text-[11px]">
                 <input 
                   type="checkbox" 
                   checked={sameAsBilledTo}
                   onChange={handleSameAsBilledTo}
                   className="rounded border-slate-300 text-brand-blue focus:ring-brand-blue w-3.5 h-3.5" 
                 /> Same as Billed To
               </span>
             </label>
             <textarea 
               name="shipped_to"
               value={formData.shipped_to}
               onChange={handleChange}
               className="w-full text-sm p-3.5 border border-slate-200 rounded-xl outline-none focus:border-brand-blue focus:ring-2 focus:ring-blue-50 resize-none h-24 bg-white shadow-sm text-slate-700 transition-all"
             />
           </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
           <FormInput
             label="Dispatch No"
             name="dispatch_no"
             value={formData.dispatch_no}
             onChange={handleChange}
             placeholder="e.g. DSP-001"
             className="bg-white shadow-sm"
           />
           <FormInput
             label="Invoice Number"
             name="invoice_no"
             value={formData.invoice_no}
             onChange={handleChange}
             placeholder="Enter invoice number"
             className="bg-white shadow-sm"
           />
           <div>
             <label className="block text-[11px] font-bold text-slate-700 mb-1.5 uppercase tracking-wider flex justify-between">
               Order / Quotation No
             </label>
             <div className="w-full text-sm font-bold px-4 border border-slate-200 bg-slate-50 text-slate-700 rounded-xl h-[42px] flex items-center shadow-sm">
               {request.customer_id?.sdId || '—'}
             </div>
           </div>
        </div>

        <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm bg-white">
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
             <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">Order Items</span>
             <span className="bg-slate-200 text-slate-700 text-[10px] font-bold px-2 py-0.5 rounded-full">{items.length}</span>
          </div>
          <DataTable
            columns={columns}
            data={items}
            emptyMessage="No items found."
            className="text-xs border-0"
          />
        </div>
        
        <div>
           <label className="block text-xs font-bold text-slate-700 mb-1.5">Notes / Description</label>
           <textarea 
             name="notes"
             value={formData.notes}
             onChange={handleChange}
             placeholder="Any additional delivery instructions..."
             className="w-full text-sm p-3.5 border border-slate-200 rounded-xl outline-none focus:border-brand-blue focus:ring-2 focus:ring-blue-50 resize-none h-20 bg-white shadow-sm text-slate-700 transition-all"
           />
         </div>
      </div>
    </Modal>
  );
}
